import { useState } from "react";
import { Check, Link2, QrCode, Share2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { qrImageUrl } from "@/lib/qr";
import { canNativeShare, copyText, shareMessage, shareNatively } from "@/lib/share";

/**
 * Every business page carries its own code. Standing at the counter the visitor
 * holds up the page and the owner scans straight into their profile; the same
 * code then travels on to the next customer, so every scan is a new visitor the
 * guide sends that business.
 */
export default function BusinessQrCard({ name, url, className = "" }) {
  const { lang } = useLang();
  const es = lang === "es";
  const [copied, setCopied] = useState(false);
  if (!url) return null;

  const copy = async () => {
    const ok = await copyText(url);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className={"rounded-[16px] border border-[#E4E2DC] bg-white p-4 dark:border-white/[0.10] dark:bg-white/[0.04] " + className}>
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#171717] text-white dark:bg-[#f2efe9] dark:text-[#171614]">
          <QrCode className="h-4 w-4" aria-hidden="true" />
        </span>
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#171717] dark:text-[#f2efe9]">
          {es ? "Comparte este lugar" : "Share this place"}
        </p>
      </div>

      <div className="mt-3 flex items-start gap-3">
        <img
          src={qrImageUrl(url, 240)}
          alt={es ? "Código QR de " + name : "QR code for " + name}
          width={112}
          height={112}
          loading="lazy"
          className="h-[112px] w-[112px] shrink-0 rounded-[10px] border border-black/[0.08] bg-white object-contain p-1.5"
        />
        <div className="min-w-0">
          <h2 className="text-[14px] font-extrabold leading-tight tracking-[-0.02em] text-[#171717] dark:text-[#f0ede6]">
            {name}
          </h2>
          <p className="mt-1 text-[11.5px] leading-[1.5] text-[#6d6d6a] dark:text-[#b8b3a9]">
            {es
              ? "Apunta la cámara al código y el perfil abre al instante. Pásalo a un amigo: cada escaneo le trae un cliente nuevo al negocio."
              : "Point a camera at the code and the profile opens instantly. Pass it on: every scan brings the business a new customer."}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copy}
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-[10px] border border-[#171717] px-3 text-[12px] font-bold text-[#171717] transition hover:bg-[#171717] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] dark:border-[#f2efe9] dark:text-[#f2efe9] dark:hover:bg-[#f2efe9] dark:hover:text-[#171614]"
        >
          {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Link2 className="h-4 w-4" aria-hidden="true" />}
          {copied ? (es ? "Enlace copiado" : "Link copied") : es ? "Copiar enlace" : "Copy link"}
        </button>
        {canNativeShare() && (
          <button
            type="button"
            onClick={() => shareNatively({ title: name, text: shareMessage(name), url })}
            className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#171717] px-3 text-[12px] font-bold text-white transition hover:bg-[#33332f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] dark:bg-[#f2efe9] dark:text-[#171614] dark:hover:bg-white"
          >
            <Share2 className="h-4 w-4" aria-hidden="true" /> {es ? "Compartir" : "Share"}
          </button>
        )}
      </div>
    </div>
  );
}